import { Button } from "@/components/ui/button";
import { MapPin, Calendar, Euro, Link, File, Clock } from "lucide-react";
import { AccommodationActivity } from "../models/activity.model";
import { BaseActivityCard } from "./BaseActivityCard";
import { DocumentationList } from "@/components/ui/documentation-list";
import { MediaGallery } from "@/components/ui/media-gallery";

interface AccommodationCardProps {
  activity: AccommodationActivity;
  onEdit: (activity: AccommodationActivity) => void;
  onDelete: (activity: AccommodationActivity) => void;
  onDuplicate?: (activity: AccommodationActivity) => void;
  onExpand?: () => void;
  isReadOnly?: boolean;
}

export const AccommodationCard = ({
  activity,
  onEdit,
  onDelete,
  onDuplicate,
  onExpand,
  isReadOnly = false
}: AccommodationCardProps) => {
  // Helper function to format date range
  const formatDateRange = (start_date: string, end_date: string) => {
    const start = new Date(start_date);
    const end = new Date(end_date);

    const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
    const startDay = start.getDate();
    const endDay = end.getDate();

    return `${startMonth} ${startDay} - ${endDay}`;
  };
  const renderAlwaysVisible = () => (
    <>
      {activity.address && (
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          <span>{activity.address}</span>
        </div>
      )}
      {activity.start_date && activity.end_date && (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          <span>{formatDateRange(activity.start_date, activity.end_date)}</span>
        </div>
      )}
      {activity.check_in_time && activity.check_in_time !== null && (
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          <span>{activity.check_in_time}</span>
        </div>
      )}
      {activity.check_out_time && activity.check_out_time !== null && (
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          <span>{activity.check_out_time}</span>
        </div>
      )}
      {activity.cost && activity.cost > 0 && (
        <div className="flex items-center gap-2">
          <Euro className="h-4 w-4" />
          <span>{activity.cost} (only visible to you)</span>
        </div>
      )}
    </>
  );

  const renderCollapsible = () => (
    <>
      {/* Description */}
      {activity.description && (
        <div className="mb-4">
          <span className="text-sm font-medium mb-1 block">Description</span>
          <p className="text-sm text-gray-600 leading-relaxed">{activity.description}</p>
        </div>
      )}

      {/* Check-in/Check-out Details */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        {activity.check_in_time && (
          <div>
            <span className="text-sm font-medium mb-1 block">Check-in</span>
            <p className="text-sm text-gray-600">{activity.check_in_time}</p>
          </div>
        )}
        {activity.check_out_time && (
          <div>
            <span className="text-sm font-medium mb-1 block">Check-out</span>
            <p className="text-sm text-gray-600">{activity.check_out_time}</p>
          </div>
        )}
      </div>

      {/* Check-in Note */}
      {activity.check_in_note && (
        <div className="mb-4">
          <span className="text-sm font-medium mb-1 block">Check-in Note</span>
          <p className="text-sm text-gray-600">{activity.check_in_note}</p>
        </div>
      )}

      {/* Media Gallery */}
      <MediaGallery media={activity.media || []} />

      {/* Documentation */}
      <DocumentationList documentation={activity.documentation || []} />

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <div className="flex gap-3">
          {/* Link Button */}
          {activity.link && (
            <Button
              variant="ghost"
              size="sm"
              className="rounded-xl"
              onClick={() => window.open(activity.link, '_blank')}
            >
              <Link className="h-5 w-5 mr-2 flex-shrink-0" />
              <span className="truncate max-w-[200px] inline-block">
                {activity.link}
              </span>
            </Button>
          )}
        </div>
      </div>
    </>
  );

  return (
    <BaseActivityCard
      activity={activity}
      onEdit={onEdit}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
      onExpand={onExpand}
      isReadOnly={isReadOnly}
    >
      {{
        alwaysVisible: renderAlwaysVisible(),
        collapsible: renderCollapsible()
      }}
    </BaseActivityCard>
  );
};
